import { FC, useMemo, useState } from 'react';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Button, Dialog, DialogContent, Grid, Typography } from '@mui/material';
import axios from 'axios';

import { useParams } from '@redwoodjs/router';
import { useMutation, useQuery } from '@redwoodjs/web';

import { SageConnection, SubNavigation } from 'src/components';
import Loader from 'src/components/Loader/Loader';
import ModalContent from 'src/components/ModalContent/ModalContent';
import ConfirmationModal from 'src/components/Modals/ConfirmationModal';
import ReusableGrid from 'src/components/ReusableGrid';
import Status from 'src/components/Status/Status';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { DEFAULT_BORDER, PARTNER_CONTACT_INFORMATION_SUB_NAVIGATION } from 'src/constants';
import useCreatePartnerAccountingConnection from 'src/features/finance/hooks/useCreatePartnerAccountingConnection/useCreatePartnerAccountingConnection';
import { REGENERATE_PRESIGNED_URL } from 'src/graphql/mutations/generateAwsURL.mutation';
import { GET_PRESIGNED_URL } from 'src/graphql/mutations/getPresignedURL.mutation';
import {
    UPDATE_PARTNER_BRANDING_COLOUR,
    UPDATE_PARTNER_CUSTOM_TERMS,
    UPDATE_PARTNER_LOGO,
    UPDATE_PARTNER_OF_PARTNER_LOGO,
} from 'src/graphql/mutations/partner.mutation';
import { UPDATE_PARTNER_MUTATION } from 'src/graphql/mutations/updatePartner.mutation';
import { UPDATE_PARTNER_OF_PARTNER_MUTATION } from 'src/graphql/mutations/updatePartnerOfPartner.mutation';
import { GET_PARTNER } from 'src/graphql/queries/partners.query';
import { GET_PARTNER_OF_PARTNER } from 'src/graphql/queries/subpartners.query';
import { useCreateNote } from 'src/hooks';
import { useCurrency } from 'src/hooks/useCurrency';
import { downloadDocument } from 'src/utils/utils';

import HeaderBackground from '../../assets/images/backgrounds/HeaderBackground.jpg';

import CommissionSummary from './components/CommissionSummary/CommissionSummary';
import LogoUpload from './components/LogoUpload';
import LogoUploadModalContent from './components/LogoUpload/ModalContent';
import MerchantSummary from './components/MerchantSummary/MerchantSummary';
import Notes from './components/Notes/Notes';
import OutstandingInvoices from './components/OutstandingInvoices/OutstandingInvoices';
import PaymentDetails from './components/PaymentDetails/PaymentDetails';
import ProductAndPricingOfferings from './components/ProductAndPricingOfferings/ProductAndPricingOfferings';
import SummaryOfServices from './components/SummaryOfServices/SummaryOfServices';
import { EditLogoProps, ViewPartnerProps } from './types';

const ViewPartner: FC<ViewPartnerProps> = () => {
    const [viewPaymentDetails, setViewPaymentDetails] = useState<boolean>(false);
    const [partnerOfPartnerId, setPartnerOfPartnerId] = useState(null);

    const { id: partnerIdParam, partnerOfPartner } = useParams();

    const isPartnerOfPartnerParam = useMemo(() => {
        return (String(partnerOfPartner).toLowerCase() === 'true') as boolean;
    }, [partnerOfPartner]);

    const onFetchPartnerCompleted = (data) => {
        if (isPartnerOfPartnerParam) {
            const { partnerOfPartner } = data ?? {};
            setPartnerOfPartnerId(partnerOfPartner?.id ?? null);
        }
    };

    const [regenerateUrl] = useMutation(REGENERATE_PRESIGNED_URL);
    const [getPresignedURL] = useMutation(GET_PRESIGNED_URL, {
        onError: (err) => {
            return showErrorToast(err.message);
        },
    });

    const onFetchPartnerError = (error) => {
        if (error.graphQLErrors?.length > 0) {
            error.graphQLErrors.forEach((graphQLError) => {
                showErrorToast(graphQLError?.extensions?.originalError?.message);
            });
        } else {
            showErrorToast(error.message);
        }
    };

    const {
        data: partnerDetails,
        loading: isLoadingPartner,
        refetch: refetchPartner,
    } = useQuery(isPartnerOfPartnerParam ? GET_PARTNER_OF_PARTNER : GET_PARTNER, {
        variables: { id: partnerIdParam },
        onCompleted: onFetchPartnerCompleted,
        onError: onFetchPartnerError,
    });

    const data = partnerDetails?.partnerOfPartner || partnerDetails?.partner;

    const { currencySymbol } = useCurrency(data?.countryDetails);
    const { onCreateNote } = useCreateNote({
        partnerId: isPartnerOfPartnerParam ? null : Number(partnerIdParam),
        partnerOfPartnerId:
            isPartnerOfPartnerParam && Number(partnerIdParam) ? Number(partnerOfPartnerId) : null,
        onRefetch: refetchPartner,
    });

    const [updatePartner] = useMutation(UPDATE_PARTNER_MUTATION, {
        onError: () => {
            showErrorToast('We were unable to update the partner. Please try again.');
        },
        onCompleted: () => {
            showSuccessToast('Partner updated successfully.');
            refetchPartner();
        },
    });

    const [updatePartnerOfPartner] = useMutation(UPDATE_PARTNER_OF_PARTNER_MUTATION, {
        onError: () => {
            showErrorToast('We were unable to update the partner. Please try again.');
        },
        onCompleted: () => {
            showSuccessToast('Partner updated successfully.');
            refetchPartner();
        },
    });

    const { handleCreatePartnerAccountingConnection } = useCreatePartnerAccountingConnection({
        partnerId: partnerDetails?.partner?.id,
        onRefetch: refetchPartner,
    });

    const [openCreateNoteModal, setOpenCreateNoteModal] = useState(false);
    const handleAddNoteModal = () => {
        setOpenCreateNoteModal(!openCreateNoteModal);
    };

    const handleCreateNote = async (values: { description: string; subject: string }) => {
        await onCreateNote({
            description: values?.description,
            subject: values?.subject,
        });
        handleAddNoteModal();
    };

    const handleViewPartnerAgreement = async () => {
        const fileName = data?.contract.split('/').reverse()[0];
        let mimeType = 'application/pdf';
        const [, fileExtension] = fileName.split('.');
        if (fileExtension === 'png') {
            mimeType = 'image/png';
        } else if (fileExtension === 'jpeg' || fileExtension === 'jpg') {
            mimeType = 'image/jpeg';
        }

        const {
            data: { getSecureUploadUrl },
        } = await getPresignedURL({
            variables: {
                input: {
                    fileName,
                    mimeType,
                    referer: window.location.pathname.substring(1),
                },
            },
        });

        const downLoadOptions = {
            fileUrl: getSecureUploadUrl?.viewableSignedUrl,
            fileName,
            isDownloadingToBrowser: true,
        };
        await downloadDocument(downLoadOptions, async (url: string) => {
            const { data } = await regenerateUrl({ variables: { url } });
            const presignedUrl = data?.regenerateSecureAWSUrl;
            return await axios.get(presignedUrl, { responseType: 'blob' });
        });
    };

    const updateLogoMutation = isPartnerOfPartnerParam
        ? UPDATE_PARTNER_OF_PARTNER_LOGO
        : UPDATE_PARTNER_LOGO;

    const [updateLogo] = useMutation(updateLogoMutation, {
        onCompleted: () => {
            showSuccessToast('Logo uploaded successfully');
        },
    });

    const [updateBrandColour] = useMutation(UPDATE_PARTNER_BRANDING_COLOUR, {
        onCompleted: () => {
            showSuccessToast('Branding colour updated successfully');
        },
    });

    const [updateCustomTerms] = useMutation(UPDATE_PARTNER_CUSTOM_TERMS, {
        onCompleted: () => {
            showSuccessToast('Custom terms and conditions updated successfully');
        },
    });

    const [openLogoModal, setOpenLogoModal] = useState(false);
    const handleLogoModalToggle = () => {
        setOpenLogoModal((prev: boolean) => {
            return !prev;
        });
    };

    const editLogo = async ({
        logo,
        useBranding,
        brandingColour,
        useCustomTerms,
        termAndConditions,
    }: EditLogoProps) => {
        const shouldUpdateLogo = logo !== data?.logo;
        if (shouldUpdateLogo) {
            await updateLogo({
                variables: {
                    input: {
                        id: Number.parseInt(partnerIdParam, 10),
                        logo,
                    },
                },
            });
        }

        if (!isPartnerOfPartnerParam) {
            const shouldUpdateBrandingColour =
                useBranding !== data?.useBranding || brandingColour !== data?.brandingColour;
            const shouldUpdateCustomTerms =
                useCustomTerms !== data?.useCustomTerms ||
                termAndConditions !== data?.termAndConditions;

            // Only hit the mutations if there is a change in the values
            if (shouldUpdateBrandingColour) {
                await updateBrandColour({
                    variables: {
                        input: {
                            id: Number.parseInt(partnerIdParam, 10),
                            useBranding,
                            brandingColour,
                        },
                    },
                });
            }

            if (shouldUpdateCustomTerms) {
                await updateCustomTerms({
                    variables: {
                        input: {
                            id: Number.parseInt(partnerIdParam, 10),
                            useCustomTerms,
                            termAndConditions,
                        },
                    },
                });
            }
        }
        await refetchPartner();
    };

    if (isLoadingPartner || !data) return <Loader />;

    const partnerLogoInitialValues = {
        logo: data?.logo || '',
        useBranding: !isPartnerOfPartnerParam ? data?.useBranding : false,
        brandingColour: !isPartnerOfPartnerParam && (data?.brandingColour || ''),
        useCustomTerms: !isPartnerOfPartnerParam ? data?.useCustomTerms : false,
        termAndConditions: !isPartnerOfPartnerParam && data?.termAndConditions,
    };

    return (
        <>
            <Grid
                item
                xs={12}
                sx={{
                    ...DEFAULT_BORDER,
                    height: '116px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                    padding: '12px 24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundImage: `url(${HeaderBackground})`,
                }}
                container
            >
                {/* Header Left */}
                <Grid item xs={6}>
                    <Typography
                        sx={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#1D1D1B',
                            marginBottom: '8px',
                        }}
                    >
                        {data?.business?.legalName || '' || '-'}
                    </Typography>
                    <Typography sx={{ fontSize: '21px', fontWeight: 400, color: '#F18F34' }}>
                        {isPartnerOfPartnerParam
                            ? `Partner Of : ${data?.partner?.business?.legalName || '-'}`
                            : data?.partnerAccountNumber || '-'}
                        {partnerDetails?.partner?.sageContactId &&
                            ` / Sage Contact ID: ${partnerDetails?.partner?.sageContactId}`}
                    </Typography>
                </Grid>
                {/* Header Right */}
                <Grid item xs={6} display="flex" justifyContent="right" gap={2}>
                    {!isPartnerOfPartnerParam && (
                        <SageConnection
                            sageContactId={partnerDetails?.partner?.sageContactId}
                            onRefresh={handleCreatePartnerAccountingConnection}
                        />
                    )}

                    {data && data.business && data.id && data.business.id && (
                        <Status
                            status={data?.business?.status}
                            id={data?.id}
                            partnerName={data?.business?.legalName}
                            businessId={data?.business?.id}
                            refetchPartner={refetchPartner}
                        />
                    )}
                </Grid>
            </Grid>

            <Grid
                item
                xs={12}
                sx={{
                    ...DEFAULT_BORDER,
                    height: '116px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                    padding: '24px 24px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
                container
            >
                {/* Section Left */}
                <Grid item md={5}>
                    <Typography sx={{ fontSize: '21px', fontWeight: 500, color: '#1D1D1B' }}>
                        Partner Agreement
                    </Typography>
                </Grid>

                {/* Section Right */}
                <Grid container item md={7} display="flex" justifyContent="right">
                    <Button
                        startIcon={<VisibilityOutlinedIcon />}
                        variant="npi-primary"
                        onClick={handleViewPartnerAgreement}
                        disabled={!data?.contract}
                    >
                        View
                    </Button>
                </Grid>
            </Grid>

            <Grid display="flex" flexDirection="row" gap={2} item>
                <ReusableGrid title="Partner Contact Information">
                    <SubNavigation
                        steps={PARTNER_CONTACT_INFORMATION_SUB_NAVIGATION}
                        contactInformation={[
                            data?.primaryContactInformation || {},
                            data?.billingContactInformation || {},
                            data?.serviceContactInformation || {},
                        ]}
                    />
                </ReusableGrid>

                <Grid
                    xs={6}
                    gap={2}
                    container
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                    }}
                >
                    <ReusableGrid
                        xs={12}
                        title="Partner Logo"
                        showEditIcon
                        handleEdit={handleLogoModalToggle}
                    >
                        <LogoUpload partner={data} />
                    </ReusableGrid>
                    <ReusableGrid xs={12} title="Summary of Service" hasBackGroundImage>
                        <SummaryOfServices services={data?.services} />
                    </ReusableGrid>
                    <ReusableGrid xs={12} title="Commission Summary">
                        <CommissionSummary
                            commissions={data?.commission}
                            usersCurrency={currencySymbol}
                        />
                    </ReusableGrid>
                    <ReusableGrid xs={12} title="Merchant Summary">
                        <MerchantSummary merchants={data?.merchants || []} />
                    </ReusableGrid>
                </Grid>
            </Grid>

            <Grid display="flex" flexDirection="row" gap={2} item>
                <ReusableGrid
                    xs={12}
                    title="Payment Details"
                    hasEditPaymentDetails
                    hasView
                    setViewPaymentDetails={setViewPaymentDetails}
                    handleEditPaymentDetails={(values) => {
                        const updateMutation = isPartnerOfPartnerParam
                            ? updatePartnerOfPartner
                            : updatePartner;

                        updateMutation({
                            variables: {
                                id: Number.parseInt(partnerIdParam, 10),
                                input: {
                                    bank: {
                                        bic: values.bic,
                                        iban: values.iban,
                                    },
                                },
                            },
                        });
                    }}
                    paymentDetailsInitialValues={{
                        bic: data?.bank?.bic ?? '',
                        iban: data?.bank?.iban ?? '',
                    }}
                >
                    <PaymentDetails
                        bank={data?.bank || {}}
                        viewPaymentDetails={viewPaymentDetails}
                    />
                </ReusableGrid>

                <ReusableGrid xs={12} title="Outstanding Invoices">
                    <OutstandingInvoices
                        invoices={data.invoices || []}
                        usersCurrency={currencySymbol}
                    />
                </ReusableGrid>
            </Grid>

            <ConfirmationModal
                isOpen={openLogoModal}
                modalWidth="500px"
                title="Add Partner Logo"
                message={
                    <LogoUploadModalContent
                        initialValues={partnerLogoInitialValues}
                        handleEditModalClose={handleLogoModalToggle}
                        handleEdit={({
                            logo,
                            useBranding,
                            brandingColour,
                            useCustomTerms,
                            termAndConditions,
                        }: EditLogoProps) => {
                            editLogo({
                                logo: logo.split('?')[0],
                                useBranding,
                                brandingColour,
                                useCustomTerms,
                                termAndConditions,
                            });
                        }}
                        enableBranding={!isPartnerOfPartnerParam}
                    />
                }
                hideCancelButton
                hideConfirmButton
                onCancel={handleLogoModalToggle}
            />

            <Grid display="flex" flexDirection="row" gap={2} item>
                <ReusableGrid
                    title="Notes"
                    addRecordButtonConfig={{
                        addRecordButtonText: 'Add Note',
                        addRecordCallBack: handleAddNoteModal,
                    }}
                >
                    <Notes notes={data?.notes} refetch={refetchPartner} />
                    <Dialog
                        open={openCreateNoteModal}
                        onClose={handleAddNoteModal}
                        aria-labelledby="create-note-offer-dialog"
                        sx={{
                            '& .MuiDialog-paper': {
                                width: '400px',
                            },
                        }}
                    >
                        <DialogContent>
                            <ModalContent
                                handleClose={handleAddNoteModal}
                                buttonText="Create Note"
                                header="Create Note"
                                initialValues={{
                                    description: '',
                                    subject: '',
                                }}
                                handleFormSubmit={handleCreateNote}
                                isSubmitModal
                                handleAction={() => {}}
                                message=""
                            />
                        </DialogContent>
                    </Dialog>
                </ReusableGrid>

                <ReusableGrid title="Product and Pricing Offerings">
                    <ProductAndPricingOfferings
                        offerList={data?.offer || []}
                        transactionPricingOfferList={data?.transactionPricingOffers || []}
                    />
                </ReusableGrid>
            </Grid>
        </>
    );
};

export default ViewPartner;
